import type { Workflow } from '@n8n-builder/n8n-types';
import {
  RocketIcon,
  SpinnerIcon,
  ArrowRightIcon,
} from '../ui/icons';
import { WorkflowCanvas } from '../workflow/WorkflowCanvas';
import { useSettingsStore } from '../../store/settings';
import { useUIStore } from '../../store/ui';

interface WorkflowPreviewProps {
  workflow: Workflow;
  workflowId: string | null;
  onDeploy: (workflow: Workflow, workflowId?: string) => void;
  isDeploying: boolean;
}

export function WorkflowPreview({
  workflow,
  workflowId,
  onDeploy,
  isDeploying,
}: WorkflowPreviewProps) {
  const { activeInstance } = useSettingsStore();
  const { setActiveSidePanelTab } = useUIStore();
  return (
    <div className="flex flex-1 flex-col min-h-0">
      {/* Centered header with workflow info */}
      <div className="flex-shrink-0 text-center border-b border-gray-200 px-4 py-2">
        <span className="text-sm font-medium text-gray-900">
          {workflow.name}
        </span>
        <span className="ml-2 text-xs text-gray-500">
          ({workflow.nodes.length} nodes)
        </span>
      </div>

      {/* Workflow canvas - fills remaining space */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <WorkflowCanvas workflow={workflow} tidyUp={true} />
      </div>

      {/* Footer buttons */}
      <div className="flex-shrink-0 flex gap-3 border-t border-gray-200 px-4 py-3.5">
        <button
          onClick={() => onDeploy(workflow, workflowId || undefined)}
          disabled={isDeploying}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50"
        >
          {isDeploying ? (
            <>
              <SpinnerIcon className="h-4 w-4" />
              Deploying...
            </>
          ) : (
            <>
              <RocketIcon className="h-4 w-4" />
              Deploy to {activeInstance?.name || 'n8n'}
            </>
          )}
        </button>
        <button
          onClick={() => setActiveSidePanelTab('credentials')}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
        >
          Add Credentials
          <ArrowRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
